import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ContentTile } from '@unmind/design-system';
import { ContentType, Tool } from 'Explore/types';
import { ShortCardVariants } from 'Shorts/constants';
import { tracking } from 'App/Tracking';
import RoutePath from 'App/RoutePath';

export interface ContentCardProps {
  tool: Tool;
  showDescription?: boolean;
  showCategory?: boolean;
  assetToken?: string | null;
  source: string;
  variant?: ShortCardVariants;
}

const ContentCard = ({
  tool,
  showDescription = false,
  showCategory = false,
  assetToken,
  source,
  variant = ShortCardVariants.VERTICAL,
}: ContentCardProps) => {
  const { t: translate } = useTranslation('shorts');
  const { state } = useLocation();

  const {
    id,
    title,
    slug,
    estimatedMins,
    cloudinaryThumbnail,
    exploreCategory,
    mediaType,
  } = tool;

  const location = useLocation<Location>();
  const toolMediaPlayerPath = slug
    ? `${RoutePath.Shorts}/${slug}`
    : RoutePath.Explore;

  const trackShortClick = () => {
    tracking.track('content-selected', {
      contentId: id,
      contentSlug: slug,
      contentDuration: estimatedMins,
      contentType: ContentType.short,
      source,
      contentCategoryPath: exploreCategory?.internalName,
    });
  };

  const tags = [
    translate('short_card.duration_text', {
      duration: estimatedMins?.toString(),
    }),
  ];

  return (
    <ContentTile
      to={{
        pathname: toolMediaPlayerPath,
        state: {
          ...state,
          onToolPlayerClose: {
            location: {
              pathname: location.pathname,
            },
          },
        },
      }}
      onClick={trackShortClick}
      item={{
        itemType: mediaType?.value as 'audio' | 'video',
        title: title as string,
        fallbackIcon: mediaType?.value === 'video' ? 'video-on' : 'audio',
        thumbnailUrl:
          cloudinaryThumbnail?.path && assetToken
            ? `${cloudinaryThumbnail?.path}?${assetToken}`
            : undefined,
        tags:
          showCategory && exploreCategory?.topLevelCategory?.title
            ? [...tags, exploreCategory.topLevelCategory.title]
            : tags,
      }}
      showItemDescription={showDescription}
      variant={variant}
      displayWithTextBg={true}
    />
  );
};

export default ContentCard;
